// (2026-06-09) Primitivos React reutilizables del modulo Compras.
//
// Replican EXACTAMENTE la identidad visual Sirius v2 del sistema existente
// (Clients/ModalClients.js, Clients/FormClients.js, Clients/ListClients.js,
// Products/ModalProducts.js, utils/formHelpers.js). NO usan @mui ni libreria
// externa de UI: solo Tailwind + clases `sirius-*` ya definidas en el proyecto.
//
// Reglas de oro respetadas (ver _DESIGN_GUIDE.md):
//   - Inputs de texto en MAYUSCULAS (.toUpperCase() en onChange de TextInput).
//   - Booleanos: circulo verde/rojo + Si/No (componente Badge).
//   - Botones via las MISMAS clases que utils/formHelpers.js.
//   - No se inventan clases nuevas: se copian tal cual de los archivos de
//     referencia.

import React from "react";
import {
  FORM_FOOTER_CONTAINER,
  FORM_BTN_CANCEL,
  FORM_BTN_SUBMIT,
} from "../../utils/formHelpers";

/* ------------------------------------------------------------------ *
 * Constantes de clases (copiadas literalmente de los referentes)
 * ------------------------------------------------------------------ */

// Input/Select/Textarea base (FormClients.js)
export const INPUT_BASE =
  "border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white " +
  "rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear " +
  "transition-all duration-150 disabled:bg-slate-100 disabled:cursor-not-allowed";

// Label de campo (FormClients.js)
export const LABEL_BASE = "block text-blueGray-600 text-xs font-bold mb-2";

/* ------------------------------------------------------------------ *
 * Modal — overlay -> panel sirius -> header (icono+titulo+cerrar) ->
 * body scrollable -> footer (botones / children custom).
 * Anatomia copiada de ModalProducts.js (header simple) + boton cerrar
 * heroico de ModalClients.js.
 * ------------------------------------------------------------------ */
export function Modal({
  open,
  onClose,
  title,
  icon,
  children,
  footer,
  maxWidth = "max-w-[1100px]",
}) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-center items-center bg-black/50 p-4">
      <div
        className={
          "bg-white rounded-2xl shadow-sirius-lg w-full " +
          maxWidth +
          " max-h-[90vh] flex flex-col overflow-hidden animate-sirius-fade-up"
        }
      >
        {/* HEADER */}
        <div className="relative px-6 md:px-8 py-5 border-b border-slate-200 bg-white">
          <h6 className="text-lg md:text-xl font-display font-bold text-ink-900 text-center flex items-center justify-center gap-2">
            {icon && (
              <i className={`fas ${icon} text-sirius-600`} aria-hidden="true" />
            )}
            {title}
          </h6>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Cerrar"
              className="absolute top-1/2 right-4 -translate-y-1/2 inline-flex items-center justify-center w-9 h-9 rounded-full text-ink-500 bg-slate-100 hover:bg-slate-200 hover:text-ink-900 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-sirius-400"
            >
              <i className="fas fa-times text-lg" aria-hidden="true" />
            </button>
          )}
        </div>

        {/* BODY (unico con scroll) */}
        <div className="flex-1 min-h-0 overflow-y-auto px-6 md:px-8 py-6">
          {children}
        </div>

        {/* FOOTER (fijo) */}
        {footer !== undefined ? (
          <div className="shrink-0 w-full flex justify-center gap-3 py-4 border-t border-slate-200 bg-white">
            {footer}
          </div>
        ) : (
          onClose && (
            <div className="shrink-0 w-full flex justify-center py-4 border-t border-slate-200 bg-white">
              <button
                type="button"
                onClick={onClose}
                className="inline-flex items-center gap-2 px-6 py-2.5 bg-twilight-indigo-600 hover:bg-twilight-indigo-500 text-white font-semibold rounded-xl shadow-sirius transition focus:outline-none focus-visible:ring-2 focus-visible:ring-twilight-indigo-400"
              >
                <i className="fas fa-times" aria-hidden="true" />
                Cerrar
              </button>
            </div>
          )
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Field — wrapper label + control. Por defecto ocupa una columna del
 * grid 12 (lg:w-4/12) replicando el patron `w-full lg:w-X/12 px-4`.
 * ------------------------------------------------------------------ */
export function Field({ label, children, className = "w-full lg:w-4/12 px-4", error }) {
  return (
    <div className={className}>
      <div className="relative w-full mb-3">
        {label && <label className={LABEL_BASE}>{label}</label>}
        {children}
        {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * TextInput — texto en MAYUSCULAS via .toUpperCase() (regla de oro).
 * Para tipos no textuales (number/date) no se fuerza mayuscula.
 * ------------------------------------------------------------------ */
export function TextInput({
  value,
  onChange,
  type = "text",
  placeholder,
  disabled = false,
  className = "",
  ...rest
}) {
  const handleChange = (e) => {
    if (!onChange) return;
    const raw = e.target.value;
    const next =
      type === "text" || type === undefined
        ? raw.toUpperCase()
        : raw;
    onChange(next, e);
  };

  return (
    <input
      type={type}
      value={value ?? ""}
      placeholder={placeholder}
      disabled={disabled}
      onChange={handleChange}
      className={`${INPUT_BASE} ${className}`.trim()}
      {...rest}
    />
  );
}

/* ------------------------------------------------------------------ *
 * SelectInput — acepta `options` ([{value,label}] o [{id,...}]) o
 * `children` (<option> directos).
 * ------------------------------------------------------------------ */
export function SelectInput({
  value,
  onChange,
  options,
  children,
  disabled = false,
  placeholder,
  className = "",
  ...rest
}) {
  return (
    <select
      value={value ?? ""}
      disabled={disabled}
      onChange={(e) => onChange && onChange(e.target.value, e)}
      className={`${INPUT_BASE} ${className}`.trim()}
      {...rest}
    >
      {placeholder && <option value="">{placeholder}</option>}
      {options
        ? options.map((opt) => {
            const val = opt.value !== undefined ? opt.value : opt.id;
            const lbl =
              opt.label !== undefined
                ? opt.label
                : opt.nombre !== undefined
                ? opt.nombre
                : String(val);
            return (
              <option key={val} value={val}>
                {lbl}
              </option>
            );
          })
        : children}
    </select>
  );
}

/* ------------------------------------------------------------------ *
 * Toggle — checkbox estilo formularios (form-checkbox mr-2 dentro de
 * label inline-flex), copiado de FormClients.js.
 * ------------------------------------------------------------------ */
export function Toggle({ checked, onChange, label, disabled = false }) {
  return (
    <label className="inline-flex items-center text-sm">
      <input
        type="checkbox"
        className="form-checkbox mr-2"
        checked={!!checked}
        disabled={disabled}
        onChange={(e) => onChange && onChange(e.target.checked, e)}
      />
      {label && <span className="font-bold">{label}</span>}
    </label>
  );
}

/* ------------------------------------------------------------------ *
 * Botones — clases IDENTICAS a utils/formHelpers.js (FORM_BTN_*).
 * ------------------------------------------------------------------ */
export function BtnPrimary({ onClick, children, type = "button", icon, disabled = false, className = "" }) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`${FORM_BTN_SUBMIT} ${icon ? "inline-flex items-center gap-2" : ""} ${className}`.trim()}
    >
      {icon && <i className={`fas ${icon}`} aria-hidden="true" />}
      {children}
    </button>
  );
}

export function BtnGhost({ onClick, children, type = "button", icon, disabled = false, className = "" }) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`${FORM_BTN_CANCEL} ${icon ? "inline-flex items-center gap-2" : ""} ${className}`.trim()}
    >
      {icon && <i className={`fas ${icon}`} aria-hidden="true" />}
      {children}
    </button>
  );
}

/* ------------------------------------------------------------------ *
 * IconBtn — boton de accion de fila (estilo .btn-view/.btn-edit de la
 * guia). `color` se aplica como clase de texto Tailwind.
 * ------------------------------------------------------------------ */
export function IconBtn({ icon, onClick, title, color = "text-gray-700", disabled = false, className = "" }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={title}
      disabled={disabled}
      className={`px-2 py-1 ${color} hover:opacity-75 transition disabled:opacity-50 disabled:cursor-not-allowed ${className}`.trim()}
    >
      <i className={`fas ${icon}`} aria-hidden="true" />
    </button>
  );
}

/* ------------------------------------------------------------------ *
 * Card — contenedor sirius-card (clase de lista de ListClients.js).
 * ------------------------------------------------------------------ */
export function Card({ children, className = "" }) {
  return (
    <div
      className={`relative bg-white flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded-lg bg-blueGray-100 border-0 ${className}`.trim()}
    >
      {children}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * PageHeader — header de tarjeta de lista: titulo + subtitulo + acciones.
 * Copiado de ListClients.js (rounded-t bg-white ... flex justify-between).
 * ------------------------------------------------------------------ */
export function PageHeader({ title, subtitle, icon, actions }) {
  return (
    <div className="rounded-t bg-white mb-0 px-6 py-6 flex justify-between items-center border-b">
      <div>
        <h6 className="text-blueGray-700 text-xl font-bold flex items-center gap-2">
          {icon && (
            <i className={`fas ${icon} text-sirius-600`} aria-hidden="true" />
          )}
          {title}
        </h6>
        {subtitle && (
          <p className="text-blueGray-400 text-sm mt-1">{subtitle}</p>
        )}
      </div>
      {actions && (
        <div className="flex items-center space-x-3">{actions}</div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * SectionTitle — titulo de seccion con icono + divisor (ModalClients.js).
 * ------------------------------------------------------------------ */
export function SectionTitle({ icon, children }) {
  return (
    <div className="flex items-center gap-3 mt-9 mb-4">
      <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-sirius-50 text-sirius-600 shrink-0">
        {icon && <i className={`fas ${icon} text-sm`} aria-hidden="true" />}
      </span>
      <h3 className="text-xs uppercase tracking-[0.16em] font-bold text-ink-500 whitespace-nowrap">
        {children}
      </h3>
      <span className="flex-1 h-px bg-gradient-to-r from-slate-200 to-transparent" />
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Badge — circulo verde/rojo + Si/No (regla de oro de booleanos).
 * Formato canonico de la guia (seccion 6).
 * ------------------------------------------------------------------ */
export function Badge({ ok, labelOk = "Si", labelNo = "No" }) {
  return ok ? (
    <span className="whitespace-nowrap">
      <i className="fas fa-circle text-emerald-500 mr-2" aria-hidden="true" />
      {labelOk}
    </span>
  ) : (
    <span className="whitespace-nowrap">
      <i className="fas fa-circle text-red-500 mr-2" aria-hidden="true" />
      {labelNo}
    </span>
  );
}

/* ------------------------------------------------------------------ *
 * Table / Th / Td — tabla simple con el wrapper scrollable de la guia.
 * (Para listados con DataTable usar el patron jQuery existente; esto es
 * para tablas estaticas o de detalle dentro de Compras.)
 * ------------------------------------------------------------------ */
export function Table({ children, className = "" }) {
  return (
    <div className="block w-full overflow-x-auto px-4 py-4">
      <table className={`items-center w-full bg-transparent border-collapse ${className}`.trim()}>
        {children}
      </table>
    </div>
  );
}

export function Th({ children, className = "", ...rest }) {
  return (
    <th
      className={`px-4 py-3 text-xs uppercase font-semibold text-left text-blueGray-500 border-b border-blueGray-200 ${className}`.trim()}
      {...rest}
    >
      {children}
    </th>
  );
}

export function Td({ children, className = "", ...rest }) {
  return (
    <td
      className={`px-4 py-3 text-sm text-blueGray-700 border-b border-blueGray-100 align-middle ${className}`.trim()}
      {...rest}
    >
      {children}
    </td>
  );
}
